import { useState } from 'react';
import { Container } from '@geberit/gdds';

// styles
import styles from './image-gallery.module.scss';

// types
import type { ImageGalleryProps, OverlayGroup } from '../types';

// components
import { Overlay } from './overlay';
import { InlineEdit } from '../../../ContentCreator/InlineEdit';
import { Wrapper } from './wrapper';
import { GalleryItem } from './gallery-item';
import { Headline } from '../../headline/headline';

// utils
import { classNameBuilder } from 'utils/classNameBuilder';
import { buildSize, gridSizes, themeToGridSize } from 'utils/gridSize';
import { useSectionId } from 'utils/hooks/use-section-id';
import { Formats } from '../../headline/headlines.types';
import key from 'weak-key';
import { isEmpty } from 'utils/is-empty';
import { useThemeName } from 'utils/hooks/use-theme';

export function GroupImageGallery({
  title,
  subtitle,
  imageGallery,
  text,
  emphasize,
  contentIndex,
  backgroundColor,
  anchor,
  titleInAnchor,
}: Readonly<ImageGalleryProps>) {
  const sectionId = useSectionId(title, anchor ?? '', titleInAnchor, contentIndex);
  const theme = useThemeName();

  const [currentItem, setCurrentItem] = useState<OverlayGroup | undefined>(undefined);

  const galleryContainerSize =
    imageGallery.length > 2
      ? buildSize(gridSizes[themeToGridSize[`${theme}_CAROUSEL`]])
      : buildSize(gridSizes[themeToGridSize[theme]]);

  return (
    <section
      className={classNameBuilder(styles.imageGallery, styles[`${backgroundColor}Background`])}
      id={sectionId}
    >
      <Container className={styles.carouselContainer} maxContentWidth={galleryContainerSize}>
        <Headline
          title={title}
          subtitle={subtitle}
          text={text}
          tag={Formats.h2}
          className={styles.headline}
          textClassName={styles.intro}
          titlePreviewId="#st_titleText"
          subtitlePreviewId="#st_subtitle"
          introPreviewId="#st_content"
          textPreviewId="#st_text"
          emphasize={emphasize}
        />
        <InlineEdit previewId="#st_imageGallery">
          {!isEmpty(imageGallery) && (
            <Wrapper imageGallery={imageGallery}>
              {imageGallery.map((item) => (
                <GalleryItem key={key(item)} item={item} setCurrentItem={setCurrentItem} />
              ))}
            </Wrapper>
          )}
        </InlineEdit>

        {currentItem && (
          <div className={styles.outerOverlay}>
            <Overlay
              title={currentItem.title}
              subtitle={currentItem.subtitle}
              emphasize={currentItem.emphasize}
              text={currentItem.text}
              onClose={() => setCurrentItem(undefined)}
            />
          </div>
        )}
      </Container>
    </section>
  );
}
